// Telecalling/frontend/src/pages/dashboard/MyTeamPage.jsx
// Dedicated management page for RBAC roles with can_manage_roles=true
// Features: Sub-role management, lead distribution, team analytics

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, UserPlus, Shield, ArrowRight, RefreshCw,
  AlertCircle, CheckCircle, Eye, EyeOff,
  Save, X, Plus, Edit2, Trash2, BarChart2, GitBranch, Inbox, ArrowLeft
} from 'lucide-react';
import api from '../services/api';
import { useParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import LeadManagement from '../components/LeadManagement';
import './MyTeamPage.css';

const MyTeamPage = () => {
  const { clientId } = useParams();
  // ─── Auth Context ──────────────────────────────────────────────────────────
  const rbacSession = JSON.parse(sessionStorage.getItem('rbac_session') || '{}');
  let myRoleId = rbacSession.role_id;
  let myRoleName = rbacSession.role_name;
  if (rbacSession.token) {
    try {
      const payload = JSON.parse(atob(rbacSession.token.split('.')[1]));
      myRoleId = payload.role_id || myRoleId;
      myRoleName = payload.role_name || myRoleName;
    } catch (e) { }
  }

  // ─── State ─────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('analytics');
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [leadSummary, setLeadSummary] = useState(null);
  const [sections, setSections] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [viewingChildLeads, setViewingChildLeads] = useState(null);
  const [viewingFullTeam, setViewingFullTeam] = useState(false);

  // Sub-role form state
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [subRoleForm, setSubRoleForm] = useState({
    role_name: '',
    username: '',
    email: '',
    password: '',
    extension_number: '',
    extension_password: '',
    session_duration_hours: 8,
    role_description: '',
    can_manage_roles: false,
    sections: []
  });

  // Lead distribution state
  const [distributing, setDistributing] = useState(false);
  const [distributions, setDistributions] = useState({});
  const [distMode, setDistMode] = useState('custom'); // 'equal' | 'custom'

  // ─── Data Loading ──────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!myRoleId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [rolesRes, summaryRes, sectionsRes] = await Promise.all([
        api.get('/rbac/roles'),
        api.get(`/rbac/roles/${myRoleId}/lead-summary`),
        api.get('/rbac/sections')
      ]);
      if (rolesRes.data.success) setChildren(rolesRes.data.data);
      if (summaryRes.data.success) setLeadSummary(summaryRes.data.data);
      if (sectionsRes.data.success) {
        setSections(sectionsRes.data.data);
      }
    } catch (err) {
      showMessage('error', 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [myRoleId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Init equal distribution when mode changes
  useEffect(() => {
    if (distMode === 'equal' && leadSummary && children.length > 0) {
      const perRole = Math.floor(leadSummary.own_lead_count / children.length);
      const newDist = {};
      children.forEach((c, i) => {
        newDist[c.id] = i === children.length - 1
          ? leadSummary.own_lead_count - perRole * (children.length - 1)
          : perRole;
      });
      setDistributions(newDist);
    }
  }, [distMode, leadSummary, children]);

  // ─── Helpers ───────────────────────────────────────────────────────────────
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const totalDistributed = Object.values(distributions).reduce((s, v) => s + (parseInt(v) || 0), 0);
  const remainingLeads = (leadSummary?.own_lead_count || 0) - totalDistributed;

  const resetSubRoleForm = () => {
    setSubRoleForm({
      role_name: '', username: '', email: '', password: '', extension_number: '', extension_password: '',
      session_duration_hours: 8, role_description: '',
      can_manage_roles: false, sections: []
    });
    setEditingRole(null);
    setShowForm(false);
    setShowPassword(false);
  };

  const toggleSection = (key) => {
    setSubRoleForm(prev => {
      const exists = prev.sections.includes(key);
      return { ...prev, sections: exists ? prev.sections.filter(k => k !== key) : [...prev.sections, key] };
    });
  };

  // ─── Sub-Role CRUD ─────────────────────────────────────────────────────────
  const handleSubRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...subRoleForm };
      // backend will force parent_role_id = myRoleId for RBAC callers
      if (editingRole) {
        const res = await api.put(`/rbac/roles/${editingRole.id}`, payload);
        if (res.data.success) { showMessage('success', 'Sub-role updated'); loadData(); resetSubRoleForm(); }
      } else {
        const res = await api.post('/rbac/roles', payload);
        if (res.data.success) { showMessage('success', 'Sub-role created'); loadData(); resetSubRoleForm(); }
      }
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to save sub-role');
    }
  };

  const handleEditSubRole = async (role) => {
    try {
      const res = await api.get(`/rbac/roles/${role.id}`);
      if (res.data.success) {
        const d = res.data.data;
        setSubRoleForm({
          role_name: d.role_name,
          username: d.username,
          email: d.email || '',
          password: '',
          extension_number: d.extension_number || '',
          extension_password: d.extension_password || '',
          session_duration_hours: d.session_duration_hours || 8,
          role_description: d.role_description || '',
          can_manage_roles: d.can_manage_roles || false,
          sections: d.sections?.map(s => s.section_key || s) || []
        });
        setEditingRole(role);
        setShowForm(true);
        setActiveTab('subroles');
      }
    } catch (err) {
      showMessage('error', 'Failed to load role details');
    }
  };

  const handleDeleteSubRole = async (roleId, roleName) => {
    if (!window.confirm(`Archive sub-role "${roleName}"?\n\nRole cannot be archived if it owns leads or has child roles.`)) return;
    try {
      const res = await api.delete(`/rbac/roles/${roleId}`);
      if (res.data.success) { showMessage('success', 'Sub-role archived'); loadData(); }
    } catch (err) {
      const d = err.response?.data;
      if (d?.code === 'HAS_LEADS') showMessage('error', `Cannot archive: Sub-role owns ${d.lead_count} lead(s)`);
      else if (d?.code === 'HAS_CHILDREN') showMessage('error', `Cannot archive: Sub-role has ${d.child_count} child role(s)`);
      else showMessage('error', d?.error || 'Failed to archive sub-role');
    }
  };

  // ─── Lead Distribution ─────────────────────────────────────────────────────
  const handleDistribute = async () => {
    const assignments = Object.entries(distributions)
      .map(([roleId, count]) => ({ roleId, count: parseInt(count) || 0 }))
      .filter(a => a.count > 0);

    if (assignments.length === 0) return showMessage('error', 'No leads to assign');
    if (totalDistributed > (leadSummary?.own_lead_count || 0)) {
      return showMessage('error', `Cannot assign ${totalDistributed} leads — you only own ${leadSummary?.own_lead_count}`);
    }

    setDistributing(true);
    try {
      const res = await api.post('/leads/assign-bulk', { assignments });
      if (res.data.success) {
        showMessage('success', `Successfully distributed ${res.data.assignedCount} leads`);
        setDistributions({});
        loadData();
      }
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Distribution failed');
    } finally {
      setDistributing(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', fontFamily: 'Inter, sans-serif' }}>
        <TopBar clientId={clientId} />
        <div className="myteam-loading">
          <RefreshCw className="myteam-spin" size={28} />
          <span>Loading your team...</span>
        </div>
      </div>
    );
  }

  if (!myRoleId) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', fontFamily: 'Inter, sans-serif' }}>
        <TopBar clientId={clientId} />
        <div className="myteam-root" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', maxWidth: '500px' }}>
            <Shield size={48} style={{ color: '#8b5cf6', marginBottom: '1rem', margin: '0 auto' }} />
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>Sub-Role Feature</h2>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.5' }}>
              The "My Team" page is designed for delegated Managers and Team Leads to manage their downstream sub-roles.
              <br /><br />
              As a Super Admin, please use the <strong>Profile Setup &gt; Module Requirements &gt; Configure RBAC</strong> menu to manage all roles globally.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', fontFamily: 'Inter, sans-serif' }}>
      <TopBar clientId={clientId} />
      {viewingChildLeads ? (
        <div style={{ padding: '20px' }}>
          <button
            onClick={() => setViewingChildLeads(null)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 16px', backgroundColor: 'white',
              border: '1px solid #e5e7eb', borderRadius: '8px',
              cursor: 'pointer', marginBottom: '20px',
              fontWeight: '600', color: '#4b5563', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            <ArrowLeft size={16} /> Back to My Team
          </button>
          <LeadManagement
            user={rbacSession}
            assignedOnly={true}
            rbacRoleId={viewingChildLeads.id}
            viewMode="hierarchy-readonly"
            roleName={viewingChildLeads.role_name}
          />
        </div>
      ) : viewingFullTeam ? (
        <div style={{ padding: '20px' }}>
          <button
            onClick={() => setViewingFullTeam(false)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 16px', backgroundColor: 'white',
              border: '1px solid #e5e7eb', borderRadius: '8px',
              cursor: 'pointer', marginBottom: '20px',
              fontWeight: '600', color: '#4b5563', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            <ArrowLeft size={16} /> Back to My Team
          </button>
          <LeadManagement
            user={rbacSession}
            rbacRoleId={myRoleId}
            scope="hierarchy"
            readonly={true}
          />
        </div>
      ) : (

        <div className="myteam-root" >
          {/* Header */}
          <div className="myteam-header">
            <div className="myteam-header-info">
              <div className="myteam-header-icon"><GitBranch size={22} /></div>
              <div>
                <h1 className="myteam-title">My Team</h1>
                <p className="myteam-subtitle">
                  Managing as <strong>{myRoleName}</strong> · {children.length} sub-role{children.length !== 1 ? 's' : ''} · {leadSummary?.own_lead_count || 0} leads in your pool
                </p>
              </div>
            </div>
            <button className="myteam-refresh-btn" onClick={loadData} title="Refresh">
              <RefreshCw size={16} />
            </button>
          </div>

          {/* Message Banner */}
          {message.text && (
            <div className={`myteam-message myteam-message--${message.type}`}>
              {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              {message.text}
            </div>
          )}

          {/* Tabs */}
          <div className="myteam-tabs">
            {[
              { id: 'analytics', label: 'Team Analytics', icon: <BarChart2 size={15} /> },
              { id: 'subroles', label: 'Sub-Roles', icon: <Users size={15} /> },
              { id: 'distribute', label: 'Lead Distribution', icon: <ArrowRight size={15} /> }
            ].map(tab => (
              <button
                key={tab.id}
                className={`myteam-tab ${activeTab === tab.id ? 'myteam-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* ── TAB: Analytics ── */}
          {activeTab === 'analytics' && (
            <div className="myteam-section">
              {/* Lead summary cards */}
              <div className="myteam-cards">
                <div className="myteam-card">
                  <div className="myteam-card-icon-wrapper">
                    <Inbox size={24} />
                  </div>
                  <div className="myteam-card-content">
                    <div className="myteam-card-label">Your Lead Pool</div>
                    <div className="myteam-card-value">{leadSummary?.own_lead_count || 0}</div>
                    <div className="myteam-card-sub">
                      <Inbox size={14} /> Available to assign
                    </div>
                  </div>
                </div>
                <div className="myteam-card">
                  <div className="myteam-card-icon-wrapper">
                    <Users size={24} />
                  </div>
                  <div className="myteam-card-content">
                    <div className="myteam-card-label">Assigned to Team</div>
                    <div className="myteam-card-value">{leadSummary?.allocated_to_children || 0}</div>
                    <div className="myteam-card-sub">
                      <Users size={14} /> Across {children.length} sub-roles
                    </div>
                  </div>
                </div>
                <div className="myteam-card">
                  <div className="myteam-card-icon-wrapper">
                    <BarChart2 size={24} />
                  </div>
                  <div className="myteam-card-content">
                    <div className="myteam-card-label">Total Under Management</div>
                    <div className="myteam-card-value">{leadSummary?.total_under_management || 0}</div>
                    <div className="myteam-card-sub">
                      <BarChart2 size={14} /> Your pool + team's pool
                    </div>
                  </div>
                </div>
                <div className="myteam-card">
                  <div className="myteam-card-icon-wrapper">
                    <GitBranch size={24} />
                  </div>
                  <div className="myteam-card-content">
                    <div className="myteam-card-label">Total Team Leads</div>
                    <div className="myteam-card-value" style={{ color: '#7c3aed' }}>{leadSummary?.recursive_total_leads ?? '…'}</div>
                    <div className="myteam-card-sub">
                      <GitBranch size={14} /> All downstream combined
                    </div>
                  </div>
                </div>
              </div>

              {/* View Full Team Button */}
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="myteam-btn myteam-btn--primary"
                  onClick={() => setViewingFullTeam(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <Eye size={15} /> View Full Team Leads ({leadSummary?.recursive_total_leads ?? '…'})
                </button>
              </div>

              {/* Children breakdown */}
              <h3 className="myteam-section-title">Sub-Role Breakdown</h3>
              {children.length === 0 ? (
                <div className="myteam-empty">
                  <Users size={36} />
                  <p>No sub-roles yet. Create your first sub-role in the Sub-Roles tab.</p>
                </div>
              ) : (
                <div className="myteam-role-list">
                  {(leadSummary?.children || children).map(child => (
                    <div key={child.id} className="myteam-role-row">
                      <div className="myteam-role-info">
                        <div className="myteam-role-name">
                          <span className="myteam-connector">└─</span>
                          {child.role_name}
                          {child.can_manage_roles && (
                            <span className="myteam-badge myteam-badge--purple" title="Can create sub-roles">
                              <Shield size={10} /> Delegated
                            </span>
                          )}
                          {!child.is_active && (
                            <span className="myteam-badge myteam-badge--gray">Inactive</span>
                          )}
                        </div>
                        <div className="myteam-role-username">@{child.username}</div>
                      </div>
                      <div className="myteam-role-stats">
                        <div className="myteam-stat">
                          <Inbox size={13} />
                          <span>{child.lead_count ?? 0} leads</span>
                        </div>
                      </div>
                      <div className="myteam-role-bar-wrap" style={{ flex: 1 }}>
                        <div
                          className="myteam-role-bar"
                          style={{
                            width: leadSummary?.total_under_management > 0
                              ? `${Math.round(((child.lead_count ?? 0) / leadSummary.total_under_management) * 100)}%`
                              : '0%'
                          }}
                        />
                      </div>
                      <div style={{ marginLeft: '16px' }}>
                        <button
                          className="myteam-btn myteam-btn--ghost"
                          onClick={() => setViewingChildLeads(child)}
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Eye size={14} style={{ marginRight: '4px' }} /> View Leads
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── TAB: Sub-Roles ── */}
          {activeTab === 'subroles' && (
            <div className="myteam-section">
              <div className="myteam-section-header">
                <h3 className="myteam-section-title" style={{ margin: 0 }}>Your Sub-Roles</h3>
                {!showForm && (
                  <button className="myteam-btn myteam-btn--primary" onClick={() => setShowForm(true)}>
                    <Plus size={15} /> Create Sub-Role
                  </button>
                )}
              </div>

              {/* Create/Edit Form */}
              {showForm && (
                <form className="myteam-form" onSubmit={handleSubRoleSubmit}>
                  <div className="myteam-form-header">
                    <h4>{editingRole ? `Edit: ${editingRole.role_name}` : 'New Sub-Role'}</h4>
                    <button type="button" className="myteam-icon-btn" onClick={resetSubRoleForm}><X size={18} /></button>
                  </div>

                  <div className="myteam-form-grid">
                    <div className="myteam-field">
                      <label>Role Name <span className="req">*</span></label>
                      <input
                        type="text"
                        value={subRoleForm.role_name}
                        onChange={e => setSubRoleForm({ ...subRoleForm, role_name: e.target.value })}
                        placeholder="e.g., Executive, Team Lead"
                        required
                        className="myteam-input"
                      />
                    </div>
                    <div className="myteam-field">
                      <label>Username <span className="req">*</span></label>
                      <input
                        type="text"
                        value={subRoleForm.username}
                        onChange={e => setSubRoleForm({ ...subRoleForm, username: e.target.value })}
                        placeholder="login username"
                        required
                        className="myteam-input"
                      />
                    </div>
                    <div className="myteam-field">
                      <label>Email</label>
                      <input
                        type="email"
                        value={subRoleForm.email}
                        onChange={e => setSubRoleForm({ ...subRoleForm, email: e.target.value })}
                        placeholder="optional"
                        className="myteam-input"
                      />
                    </div>
                    <div className="myteam-field">
                      <label>Password {!editingRole && <span className="req">*</span>}</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={subRoleForm.password}
                          onChange={e => setSubRoleForm({ ...subRoleForm, password: e.target.value })}
                          placeholder={editingRole ? 'Leave blank to keep existing' : 'Enter password'}
                          required={!editingRole}
                          className="myteam-input"
                          style={{ paddingRight: '2.5rem' }}
                        />
                        <button type="button" className="myteam-pw-toggle" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                    </div>
                    <div className="myteam-field">
                        <label>PBX Extension (2001–2030)</label>
                        <input
                          type="text"
                          value={subRoleForm.extension_number}
                          onChange={e => setSubRoleForm({ ...subRoleForm, extension_number: e.target.value })}
                          placeholder="e.g. 2001"
                          className="myteam-input"
                        />
                      </div>
                      <div className="myteam-field">
                        <label>Extension Password (Optional)</label>
                        <input
                          type="text"
                          value={subRoleForm.extension_password}
                          onChange={e => setSubRoleForm({ ...subRoleForm, extension_password: e.target.value })}
                          placeholder="e.g. secret123"
                          className="myteam-input"
                        />
                      </div>
                      <div className="myteam-field">
                        <label>Session Duration (hours)</label>
                      <input
                        type="number" min="1" max="24"
                        value={subRoleForm.session_duration_hours}
                        onChange={e => setSubRoleForm({ ...subRoleForm, session_duration_hours: parseInt(e.target.value) || 8 })}
                        className="myteam-input"
                      />
                    </div>
                  </div>

                  <div className="myteam-field" style={{ marginTop: '0.75rem' }}>
                    <label>Description (Optional)</label>
                    <textarea
                      value={subRoleForm.role_description}
                      onChange={e => setSubRoleForm({ ...subRoleForm, role_description: e.target.value })}
                      placeholder="Brief description..."
                      rows={2}
                      className="myteam-input"
                      style={{ resize: 'vertical' }}
                    />
                  </div>

                  {/* Delegation Toggle */}
                  <div className={`myteam-delegation-toggle ${subRoleForm.can_manage_roles ? 'active' : ''}`}>
                    <div className="myteam-delegation-info">
                      <Shield size={14} />
                      <div>
                        <strong>Can Create Sub-Roles & Assign Leads</strong>
                        <p>If enabled, this sub-role can create its own child roles and distribute leads.</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`myteam-toggle-btn ${subRoleForm.can_manage_roles ? 'on' : ''}`}
                      onClick={() => setSubRoleForm({ ...subRoleForm, can_manage_roles: !subRoleForm.can_manage_roles })}
                    >
                      <span className="myteam-toggle-knob" />
                    </button>
                  </div>

                  {/* Section Permissions Removed for Whitelabel Simplification */}

                  <div className="myteam-form-actions">
                    <button type="button" className="myteam-btn myteam-btn--ghost" onClick={resetSubRoleForm}>Cancel</button>
                    <button type="submit" className="myteam-btn myteam-btn--primary">
                      <Save size={15} /> {editingRole ? 'Update Sub-Role' : 'Create Sub-Role'}
                    </button>
                  </div>
                </form>
              )}

              {/* Role List */}
              {!showForm && (
                children.length === 0 ? (
                  <div className="myteam-empty">
                    <UserPlus size={36} />
                    <p>No sub-roles yet. Create your first one above.</p>
                  </div>
                ) : (
                  <div className="myteam-role-list" style={{ marginTop: '1rem' }}>
                    {children.map(child => (
                      <div key={child.id} className="myteam-role-row">
                        <div className="myteam-role-info">
                          <div className="myteam-role-name">
                            {child.role_name}
                            {child.can_manage_roles && (
                              <span className="myteam-badge myteam-badge--purple"><Shield size={10} /> Delegated</span>
                            )}
                          </div>
                          <div className="myteam-role-username">@{child.username}</div>
                        </div>
                        <div className="myteam-role-stats">
                          <span className="myteam-stat"><Inbox size={13} /> {child.lead_count ?? 0} leads</span>
                          <span className="myteam-stat"><Users size={13} /> {child.child_count ?? 0} sub-roles</span>
                        </div>
                        <div className="myteam-role-actions">
                          <button className="myteam-icon-btn" onClick={() => handleEditSubRole(child)} title="Edit"><Edit2 size={14} /></button>
                          <button className="myteam-icon-btn myteam-icon-btn--danger" onClick={() => handleDeleteSubRole(child.id, child.role_name)} title="Archive"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {/* ── TAB: Lead Distribution ── */}
          {activeTab === 'distribute' && (
            <div className="myteam-section">
              {/* Lead pool info */}
              <div className="myteam-pool-banner">
                <Inbox size={18} />
                <div>
                  <strong>Your Lead Pool: {leadSummary?.own_lead_count || 0} leads available</strong>
                  <p>You can only distribute leads currently in your pool. Leads move from your pool to the sub-role.</p>
                </div>
              </div>

              {leadSummary?.own_lead_count === 0 && (
                <div className="myteam-empty">
                  <Inbox size={36} />
                  <p>Your lead pool is empty. Ask your administrator to assign leads to you first.</p>
                </div>
              )}

              {children.length === 0 && (
                <div className="myteam-empty">
                  <Users size={36} />
                  <p>No sub-roles to distribute to. Create sub-roles first in the Sub-Roles tab.</p>
                </div>
              )}

              {leadSummary?.own_lead_count > 0 && children.length > 0 && (
                <>
                  {/* Mode selector */}
                  <div className="myteam-mode-row">
                    <button
                      className={`myteam-mode-btn ${distMode === 'equal' ? 'active' : ''}`}
                      onClick={() => setDistMode('equal')}
                    >
                      Equal Split
                    </button>
                    <button
                      className={`myteam-mode-btn ${distMode === 'custom' ? 'active' : ''}`}
                      onClick={() => setDistMode('custom')}
                    >
                      Custom
                    </button>
                  </div>

                  {/* Distribution table */}
                  <div className="myteam-dist-table">
                    {children.map(child => (
                      <div key={child.id} className="myteam-dist-row">
                        <div className="myteam-dist-role">
                          <div className="myteam-role-name">{child.role_name}</div>
                          <div className="myteam-role-username">Currently owns {child.lead_count ?? 0} leads</div>
                        </div>
                        <div className="myteam-dist-input-wrap">
                          <input
                            type="number"
                            min="0"
                            max={leadSummary?.own_lead_count || 0}
                            value={distributions[child.id] || ''}
                            onChange={e => {
                              if (distMode === 'equal') setDistMode('custom');
                              setDistributions({ ...distributions, [child.id]: parseInt(e.target.value) || 0 });
                            }}
                            placeholder="0"
                            className="myteam-dist-input"
                          />
                          <span className="myteam-dist-unit">leads</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary bar */}
                  <div className={`myteam-dist-summary ${remainingLeads < 0 ? 'over' : ''}`}>
                    <span>Distributing: <strong>{totalDistributed}</strong></span>
                    <span>Available: <strong>{leadSummary?.own_lead_count || 0}</strong></span>
                    <span className={remainingLeads < 0 ? 'myteam-over' : ''}>
                      Remaining in your pool: <strong>{Math.max(0, remainingLeads)}</strong>
                      {remainingLeads < 0 && <span className="myteam-over-badge"> ⚠ Over by {Math.abs(remainingLeads)}</span>}
                    </span>
                  </div>

                  <button
                    className="myteam-btn myteam-btn--primary"
                    onClick={handleDistribute}
                    disabled={distributing || totalDistributed === 0 || remainingLeads < 0}
                    style={{ marginTop: '1rem', width: '100%' }}
                  >
                    {distributing ? <><RefreshCw size={15} className="myteam-spin" /> Distributing...</> : <><ArrowRight size={15} /> Distribute {totalDistributed} Leads</>}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyTeamPage;
